import { promiseDelay } from "./promise";

export type RetryDelay<T> =
  | number
  | "backoff"
  | ((
      tries: number,
      error: unknown,
      result: T | undefined
    ) => number | Promise<number>);

export type ShouldRetry<T> = (
  tries: number,
  error: unknown,
  result: T | undefined
) => boolean | Promise<boolean>;

export interface RetryOptions<T> {
  maxAttempts?: number;
  delay?: RetryDelay<T>;
  shouldRetry?: ShouldRetry<T>;
}

const defaultShouldRetry: ShouldRetry<unknown> = (_tries, error) =>
  error !== undefined;

async function computeDelayMs<T>(
  delay: RetryDelay<T>,
  tries: number,
  error: unknown,
  result: T | undefined
): Promise<number> {
  switch (typeof delay) {
    case "number":
      return delay;
    case "function":
      return await delay(tries, error, result);
    case "string":
      // "backoff"
      return 200 * Math.pow(2, tries - 1);
  }
}

export async function retry<T>(
  op: (tries: number) => Promise<T> | T,
  options?: RetryOptions<T>
): Promise<T> {
  const maxAttempts = options?.maxAttempts ?? 3;
  const delay: RetryDelay<T> = options?.delay ?? "backoff";
  const shouldRetry = options?.shouldRetry ?? defaultShouldRetry;

  let tries = 0;
  let lastResult: T | undefined;
  let lastError: unknown;
  let hasResult = false;

  while (true) {
    tries++;
    lastResult = undefined;
    lastError = undefined;
    hasResult = false;
    try {
      lastResult = await op(tries);
      hasResult = true;
    } catch (err) {
      lastError = err;
    }

    if (tries >= maxAttempts) {
      break;
    }
    if (!(await shouldRetry(tries, lastError, lastResult))) {
      break;
    }

    await promiseDelay(await computeDelayMs(delay, tries, lastError, lastResult));
  }

  if (hasResult) {
    return lastResult as T;
  }
  throw lastError;
}
